import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  listVATReturns, createVATReturn, calculateVATReturn, calculateVATReturnFromGL,
  fileVATReturn, exportVATReturnPDF,
} from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';

function VATReturns({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [returns, setReturns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ period_start: '', period_end: '', notes: '' });
  const [expandedId, setExpandedId] = useState(null);
  const [filingRef, setFilingRef] = useState('');

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchReturns = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listVATReturns(authAxios);
      setReturns(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage('error', 'Failed to load VAT returns.');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createVATReturn(authAxios, formData);
      showFormMessage('success', 'VAT return created.');
      setShowForm(false);
      setFormData({ period_start: '', period_end: '', notes: '' });
      fetchReturns();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to create VAT return.');
    }
  };

  const handleCalculate = async (id) => {
    try {
      const updated = await calculateVATReturn(authAxios, id);
      showFormMessage('success', 'VAT return calculated from documents.');
      setReturns(prev => prev.map(r => r.id === id ? updated : r));
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Calculation failed.');
    }
  };

  const handleCalculateFromGL = async (id) => {
    try {
      const updated = await calculateVATReturnFromGL(authAxios, id);
      showFormMessage('success', 'VAT return calculated from GL.');
      setReturns(prev => prev.map(r => r.id === id ? updated : r));
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'GL calculation failed.');
    }
  };

  const handleFile = async (id) => {
    if (!(await confirm({ title: 'File VAT Return', message: 'File this VAT return? This will post the closing GL journal.', confirmText: 'File', variant: 'danger' }))) return;
    try {
      const updated = await fileVATReturn(authAxios, id, { filing_reference: filingRef });
      showFormMessage('success', 'VAT return filed successfully.');
      setReturns(prev => prev.map(r => r.id === id ? updated : r));
      setFilingRef('');
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Filing failed.');
    }
  };

  const handleExportPDF = async (id) => {
    try {
      const blob = await exportVATReturnPDF(authAxios, id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const vatReturn = returns.find(r => r.id === id);
      link.setAttribute('download', `VAT_Return_${vatReturn?.period_start}_${vatReturn?.period_end}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      showFormMessage('error', 'PDF export failed.');
    }
  };

  // Using shared formatCurrency from utils/currency

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">VAT Returns</h2>
        {canManage && (
          <button onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm">
            + New VAT Return
          </button>
        )}
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white border rounded shadow-lg p-4 mb-6">
          <h3 className="font-semibold mb-3">New VAT Return Period</h3>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Period Start *</label>
              <input type="date" value={formData.period_start}
                onChange={e => setFormData(f => ({ ...f, period_start: e.target.value }))} required
                className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Period End *</label>
              <input type="date" value={formData.period_end}
                onChange={e => setFormData(f => ({ ...f, period_end: e.target.value }))} required
                className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <input value={formData.notes}
                onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm" placeholder="Optional notes" />
            </div>
            <div className="md:col-span-3 flex gap-2">
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">Create</button>
              <button type="button" onClick={() => setShowForm(false)} className="bg-gray-200 px-4 py-2 rounded text-sm hover:bg-gray-300">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Returns list */}
      {isLoading ? <p className="text-gray-500 text-sm">Loading…</p> : returns.length === 0 ? (
        <p className="text-gray-400 italic text-sm">No VAT returns created yet.</p>
      ) : (
        <div className="space-y-3">
          {returns.map(r => (
            <div key={r.id} className="bg-white border rounded shadow-sm">
              {/* Header row */}
              <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                <div className="flex items-center gap-4">
                  <span className="font-medium text-sm">{r.period_start} – {r.period_end}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    r.status === 'FILED' ? 'bg-green-100 text-green-700' :
                    r.status === 'AMENDED' ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>{r.status_display || r.status}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">Net: <strong className={Number(r.net_vat) >= 0 ? 'text-red-600' : 'text-green-600'}>
                    {formatCurrency(Math.abs(r.net_vat))}</strong> {Number(r.net_vat) >= 0 ? 'payable' : 'refundable'}</span>
                  <span className="text-gray-400">{expandedId === r.id ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Expanded detail */}
              {expandedId === r.id && (
                <div className="border-t px-4 py-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500">Taxable Supplies</p>
                      <p className="font-medium">{formatCurrency(r.taxable_supplies)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Output VAT (Collected)</p>
                      <p className="font-medium text-red-600">{formatCurrency(r.output_vat)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Input VAT (Paid)</p>
                      <p className="font-medium text-green-600">{formatCurrency(r.input_vat)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Net VAT</p>
                      <p className={`font-bold ${Number(r.net_vat) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(r.net_vat)}
                      </p>
                    </div>
                  </div>

                  {r.exempt_supplies > 0 && (
                    <p className="text-xs text-gray-500 mb-1">Exempt Supplies: {formatCurrency(r.exempt_supplies)}</p>
                  )}
                  {r.zero_rated_supplies > 0 && (
                    <p className="text-xs text-gray-500 mb-1">Zero-Rated Supplies: {formatCurrency(r.zero_rated_supplies)}</p>
                  )}
                  {r.filing_reference && (
                    <p className="text-xs text-gray-500 mb-1">BURS Filing Ref: {r.filing_reference}</p>
                  )}
                  {r.filed_date && (
                    <p className="text-xs text-gray-500 mb-1">Filed: {r.filed_date} by {r.filed_by_username}</p>
                  )}
                  {r.notes && <p className="text-xs text-gray-500 mb-3">Notes: {r.notes}</p>}

                  {/* Actions */}
                  {canManage && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {r.status === 'DRAFT' && (
                        <>
                          <button onClick={() => handleCalculate(r.id)}
                            className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-700">
                            Calculate from Documents
                          </button>
                          <button onClick={() => handleCalculateFromGL(r.id)}
                            className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-700">
                            Calculate from GL
                          </button>
                          <div className="flex items-center gap-1">
                            <input value={filingRef} onChange={e => setFilingRef(e.target.value)}
                              className="border rounded px-2 py-1 text-xs" placeholder="BURS filing ref" style={{ width: 160 }} />
                            <button onClick={() => handleFile(r.id)}
                              className="bg-green-600 text-white px-3 py-1.5 rounded text-xs hover:bg-green-700">
                              File Return
                            </button>
                          </div>
                        </>
                      )}
                      <button onClick={() => handleExportPDF(r.id)}
                        className="bg-gray-600 text-white px-3 py-1.5 rounded text-xs hover:bg-gray-700">
                        Export PDF
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default VATReturns;
